import type { Currency } from './currency.js';
import type { InquiryDto } from './inquiry.js';
import type { OfferDto } from './offer.js';
import type { FundingStatus } from './payments.js';
import type { ProjectDeliveryMode, GovernanceProjectType } from './project-governance.js';
import type { MilestonePriority } from './project-controls.js';

export const ProjectStatus = {
  Contracting: 'Contracting',
  Kickoff: 'Kickoff',
  InProgress: 'InProgress',
  Closure: 'Closure',
  Completed: 'Completed',
}

export const ProjectStatusValues = Object.values(ProjectStatus)

export type ProjectStatus = (typeof ProjectStatus)[keyof typeof ProjectStatus]

export type ProjectLastActivityKind = 'project' | 'workspace_file' | 'comment';

export type ProjectMilestoneStatus =
  | 'NotStarted'
  | 'InProgress'
  | 'Handover'
  | 'Completed'
  | 'Blocked';

export interface ProjectFileRef {
  id: string;
  name: string;
  url?: string;
  version?: string;
}

export interface ProjectKpiDto {
  id?: string;
  description: string;
  value?: number;
  unit?: string;
}

export interface ProjectMilestone {
  id: string;
  title: string;
  startDate?: string;
  dueDate?: string;
  output?: string;
  acceptanceCriteria?: string;
  requiredCooperation?: string;
  /** Supplier note when milestone was submitted for review. */
  handoverComment?: string;
  /** Inquirer feedback when returning milestone for rework (REQUEST_CHANGES). Cleared on next handover. */
  changeRequestComment?: string;
  status: ProjectMilestoneStatus;
  assignee: string;
  files: ProjectFileRef[];
  price?: number;
  order?: number;
  confirmations: {
    consultant: boolean;
    client: boolean;
  };
  paymentPlanItemId?: string;
  /** From linked payment plan item when `projectMilestoneId` is set (workspace detail). */
  fundingStatus?: FundingStatus;
  /** PRINCE2 stage of a governed project. */
  stageId?: string | null;
  /** Internal delivery: the user who starts and hands over the milestone. */
  teamManagerUserId?: string | null;
  /** Internal delivery: the user who accepts the milestone. */
  acceptanceUserId?: string | null;
  /** Governed scope: a MUST milestone counts in the scope tolerance. */
  priority?: MilestonePriority;
  /** Latest forecast from the PM or the Team Manager. */
  forecastDueDate?: string | null;
  forecastUpdatedAt?: string | null;
  /** Working-day duration for schedule/critical path (Mon–Fri). */
  durationWorkingDays?: number | null;
  /** Governed acceptance record. */
  acceptedAt?: string | null;
  acceptedByUserId?: string | null;
  /** Workspace files that the acceptance user linked as acceptance evidence. */
  acceptanceEvidenceFileIds?: string[];
}

/** Client PM financial snapshot for a project (single GET, not on list endpoints). */
export interface ProjectFinancialOverviewDto {
  currency: Currency;
  totalBudgetGross: number;
  /** Escrow wallet balance when linked; null if no escrow account. */
  escrowBalance: number | null;
  /** Sum of payouts marked PAID_OUT for this project's payment plan(s). */
  totalPaidOut: number;
}

export const MilestoneAction = {
  MARK_IN_PROGRESS: 'MARK_IN_PROGRESS',
  MARK_HANDOVER: 'MARK_HANDOVER',
  MARK_COMPLETED: 'MARK_COMPLETED',
  REQUEST_CHANGES: 'REQUEST_CHANGES',
} as const;

export const MilestoneActionValues = Object.values(MilestoneAction);
export type MilestoneAction = (typeof MilestoneAction)[keyof typeof MilestoneAction];

export interface UpdateMilestoneDto {
  action: MilestoneAction;
  /** Only used when action is MARK_HANDOVER; ignored for other actions. */
  handoverComment?: string;
  /** Required when action is REQUEST_CHANGES; ignored for other actions. */
  changeRequestComment?: string;
  /**
   * MARK_COMPLETED of a governed milestone: workspace files of the project that prove the acceptance.
   * At least one is required for a milestone in a PRINCE2 stage.
   */
  evidenceFileIds?: string[];
}

export interface Project {
  id: string;
  title: string;
  projectCode?: string;
  status: ProjectStatus;
  currency: Currency;
  inquiryId: InquiryDto['id'] | null;
  offerId: OfferDto['id'] | null;
  projectConceptId?: string | null;
  startDate: string;
  endDate?: string;
  progress: number;
  milestones: ProjectMilestone[];
  kpis: ProjectKpiDto[];
  /** Set by PRINCE2 governance; null when the project has no governance record or no mode yet. */
  deliveryMode?: ProjectDeliveryMode | null;
  /** PRINCE2 project type, for portfolio reporting. */
  projectType?: GovernanceProjectType | null;
  /** ISO timestamp: latest of project row updates and workspace file upload/delete. */
  lastActivityAt?: string;
  /** Which source won for `lastActivityAt` (tie → project). */
  lastActivityKind?: ProjectLastActivityKind;
}

export type ProjectDto = Project & {
  offer?: {
    account?: {
      party?: {
        name: string;
      };
    };
  };
  financialOverview?: ProjectFinancialOverviewDto | null;
};

export interface CreateProjectDto {
  inquiryId: string;
  offerId: string;
  status?: ProjectStatus;
  startDate: string; // ISO YYYY-MM-DD
  endDate?: string; // ISO YYYY-MM-DD
  progress?: number; // 0 - 100
}

export const ProjectAction = {
  START_PROJECT: 'START_PROJECT',
} as const;

export const ProjectActionValues = Object.values(ProjectAction);
export type ProjectAction = (typeof ProjectAction)[keyof typeof ProjectAction];
